import { ITermsInfo } from "@/domain/entities/serviceInfo/termsInfo/termsInfo.types";
import {
  IServiceInfo,
  ISignupField,
  ISignupInfo,
} from "@/domain/entities/serviceInfo/serviceInfo.types";
import { IResultData } from "@/domain/entities/shared/result";
import TYPES from "@/ioc/identifiers";
import { container } from "@/ioc/inversify.config";
import { IServiceInfoRepository } from "@/repositories/serviceInfo/serviceInfoRepository.types";
import useSetResultCodeIfNeeded from "@/use/useSetResultCodeIfNeeded";
import { defineStore } from "pinia";
import { IOption, SERVICE_INFO, TimerStatus } from "@/types";

interface IServiceState {
  serviceInfo: IServiceInfo | undefined;
  isUpdatedServiceInfo: boolean;
  serviceUid: string;
  welcomeMessages: string[][];
  signupInfos: ISignupInfo[];
  userInfos: ISignupInfo[];
  termsList: ITermsInfo[];
  agreeCodes: ITermsInfo[];
}

const serviceInfoRepository = () => {
  return container.get<IServiceInfoRepository>(TYPES.IServiceInfoRepository);
};

export const useServiceStore = defineStore("service", {
  state: (): IServiceState => {
    return {
      serviceInfo: undefined,
      isUpdatedServiceInfo: false,
      serviceUid: "202105280554093811482471379SVC",
      welcomeMessages: [["구성원님", "반갑습니다."]],
      signupInfos: [],
      userInfos: [],
      termsList: [],
      agreeCodes: [],
    };
  },
  getters: {
    getWelcomeMessages(state) {
      return state.welcomeMessages;
    },
    getUserInfoByUseRankCode(state) {
      return (rankCode: string) => {
        return state.userInfos.find(
          (userInfo) => userInfo.useRankCode === rankCode
        );
      };
    },
    getSignupInfoByUseRankCode(state) {
      return (rankCode: string) => {
        return state.signupInfos.find(
          (signupInfo) => signupInfo.useRankCode === rankCode
        );
      };
    },
    getParentField(state) {
      return (rankCode: string, parentFieldKey: string) => {
        return state.signupInfos
          .find((signupInfo) => signupInfo.useRankCode === rankCode)
          ?.fields?.find((field) => field.key === parentFieldKey);
      };
    },
    getChildField(state) {
      return (rankCode: string, fieldKey: string) => {
        return state.signupInfos
          .find((signupInfo) => signupInfo.useRankCode === rankCode)
          ?.fields?.find((field) => field.parentFieldKey === fieldKey);
      };
    },
    getUnverifiedFields(state) {
      return (rankCode: string) => {
        return state.signupInfos
          .find((signupInfo) => signupInfo.useRankCode === rankCode)
          ?.fields?.find(
            (field) =>
              !field.authRequiredForEditYn &&
              field.verificationCodeSent &&
              field.verificationCodeRequired &&
              !field.verificationSuccess
          );
      };
    },
  },
  actions: {
    updateFieldValidity(rankCode: string, fieldKey: string, isValid: boolean) {
      this.signupInfos = this.signupInfos.map((signupInfo) => {
        if (signupInfo.useRankCode !== rankCode) return signupInfo;

        return {
          ...signupInfo,
          fields: signupInfo.fields?.map((field) => {
            return field.key === fieldKey ? { ...field, isValid } : field;
          }),
        };
      });
    },

    updateFieldOptions(rankCode: string, filedKey: string, options: IOption[]) {
      this.signupInfos = this.signupInfos.map((signupInfo) => {
        if (signupInfo.useRankCode !== rankCode) return signupInfo;

        return {
          ...signupInfo,
          fields: signupInfo.fields?.map((field) =>
            field.key === filedKey ? { ...field, options } : field
          ),
        };
      });
    },

    updateFieldTimerStatus(
      rankCode: string,
      fieldKey: string,
      timerStatus: TimerStatus
    ) {
      this.signupInfos = this.signupInfos.map((signupInfo) => {
        if (signupInfo.useRankCode !== rankCode) return signupInfo;

        return {
          ...signupInfo,
          fields: signupInfo.fields?.map((field) =>
            field.key === fieldKey ? { ...field, timerStatus } : field
          ),
        };
      });
    },

    updateFieldEdited(rankCode: string, fieldKey: string, isEdited: boolean) {
      this.signupInfos = this.signupInfos.map((signupInfo) => {
        if (signupInfo.useRankCode !== rankCode) return signupInfo;

        return {
          ...signupInfo,
          fields: signupInfo.fields?.map((field) =>
            field.key === fieldKey ? { ...field, isEdited } : field
          ),
        };
      });
    },

    updateVerficationCodeSent(
      rankCode: string,
      fieldKey: string,
      isSent: boolean
    ) {
      this.signupInfos = this.signupInfos.map((signupInfo) => {
        if (signupInfo.useRankCode !== rankCode) return signupInfo;

        return {
          ...signupInfo,
          fields: signupInfo.fields?.map((field) =>
            field.parentFieldKey === fieldKey
              ? { ...field, verificationCodeSent: isSent }
              : field
          ),
        };
      });
    },

    updateVerificationSuccess(
      rankCode: string,
      fieldKey: string,
      success: boolean
    ) {
      this.signupInfos = this.signupInfos.map((signupInfo) => {
        if (signupInfo.useRankCode !== rankCode) return signupInfo;

        return {
          ...signupInfo,
          fields: signupInfo.fields?.map((field) =>
            field.key === fieldKey
              ? { ...field, verificationSuccess: success }
              : field
          ),
        };
      });
    },

    initSignupFields(rankCode: string) {
      this.signupInfos = this.signupInfos.map((signupInfo) => {
        if (signupInfo.useRankCode !== rankCode) return signupInfo;

        const fields = signupInfo.fields?.map((field) => ({
          ...field,
          isValid: !field.parentFieldKey,
          options: undefined,
          timerStatus: undefined,
          isEdited: false,
          verificationCodeSent: false,
          verificationSuccess: false,
        }));

        return {
          ...signupInfo,
          fields,
        };
      });
    },

    createCertField(
      field: ISignupField,
      verificationCodeRequired: boolean
    ): ISignupField {
      return {
        key: field.certInputField,
        inputFields: field.certInputField ? [field.certInputField] : [],
        type: field.certInputField,
        label: field.certLabel,
        placeholder: field.certPlaceholder,
        parentFieldKey: field.key,
        isRequired: true,
        isValid: false,
        isEditable: true,
        isEdited: false,
        verificationCodeSent: false,
        verificationCodeRequired,
        verificationSuccess: false,
      };
    },

    parseSignupInfos(signupInfos: ISignupInfo[]): ISignupInfo[] {
      const parsedSignupInfos = signupInfos.map((signupInfo) => {
        if (!signupInfo.fields) return { ...signupInfo };

        const newFields: ISignupField[] = [];
        signupInfo.fields?.forEach((field) => {
          newFields.push(field);
          if (field.key === "JOIN.MEMBER_PHONE" && field.authRequiredForEditYn)
            return;

          if (field.isCertRequired) {
            const verificationCodeRequired =
              field.key === "JOIN.MEMBER_EMAIL" ||
              field.key === "JOIN.MEMBER_PHONE";

            newFields.push(
              this.createCertField(field, verificationCodeRequired)
            );
          }
        });
        signupInfo.fields = [...newFields];

        return { ...signupInfo };
      });

      return [...parsedSignupInfos];
    },

    async getServiceInfo(): Promise<IResultData<IServiceInfo>> {
      const serviceInfoResult: IResultData<IServiceInfo> = {
        isSuccess: false,
      };

      await serviceInfoRepository()
        .getServiceInfo()
        .then((result) => {
          result
            .map((serviceInfo) => {
              this.serviceInfo = serviceInfo;
              this.isUpdatedServiceInfo = true;
              this.serviceUid = serviceInfo.id;
              this.welcomeMessages = serviceInfo.welcomeMessages;
              this.userInfos =
                JSON.parse(JSON.stringify(serviceInfo.signupInfos ?? "")) ?? [];
              this.signupInfos = this.parseSignupInfos(
                serviceInfo.signupInfos ? [...serviceInfo.signupInfos] : []
              );
              window.sessionStorage.setItem(
                SERVICE_INFO,
                JSON.stringify(serviceInfo)
              );
              this.termsList = serviceInfo.termsList ?? [];
              this.agreeCodes = serviceInfo.agreeCodes ?? [];
              document.title = serviceInfo?.name ?? "";
              serviceInfoResult.isSuccess = true;
              serviceInfoResult.data = serviceInfo;
            })
            .mapErr((error) => {
              useSetResultCodeIfNeeded(serviceInfoResult, error);
              serviceInfoResult.errorMessage = error.message;
            });
        })
        .catch((error) => {
          serviceInfoResult.errorMessage = error.message;
        });

      return serviceInfoResult;
    },
  },
});
